import React from "react";
import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  GridItemProps,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import OrderHistoryInvoiceAccordion from "@/features/my-order/features/order-history/components/invoice-section/OrderHistoryInvoiceAccordion";
import OrderHistorySubPriceInfo from "@/features/my-order/features/order-history/components/OrderHistorySubPriceInfo";
import OrderHistoryInvoiceToggleBar from "@/features/my-order/features/order-history/components/OrderHistoryInvoiceToggleBar";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useOrderHistoryProformaInvoice from "@/features/my-order/features/order-history/hooks/useOrderHistoryProformaInvoice";
import { extractTransportationFromOrderHistory } from "@/features/my-order/features/order-history/utils";
import { toCamelKeyObject } from "@/utils/case";
import { formatDateToYMDEGeneral } from "@/shared/utils";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";

function OrderHistoryInvoiceIncidentalExpensesArticle() {
  const { tTitle, tCurrencyKrw } = useI18nHelper();
  const { data, incidentalExpenses } = useOrderHistoryProformaInvoice();
  const {
    data: downloadUnoFileData,
    fire: fireDownloadUnoFile,
    isLoading: downloadUnoFileIsLoading,
    clear: clearDownloadUnoFile,
    error: downloadUnoFileError,
  } = useDownloadUnoFile();

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      <Flex gap={"4px"}>
        <CustomIcon src={"receipt"} width={24} height={24} color={"#8F8F8C"} />
        <Text textStyle={"body-lg-semi-bold"} color={"#444444"}>
          {`${tTitle(
            "order_history:invoice.incidental_expenses"
          )} (${tCurrencyKrw(incidentalExpenses ?? 0)})`}
        </Text>
      </Flex>
      <Flex flexDirection={"column"} gap={"8px"}>
        {data?.transportations.length === 0 && (
          <OrderHistoryInvoiceToggleBar
            name={tTitle("common:shipping")}
            hasData={false}
          >
            {({ isOpen }) => (
              <Flex flexDirection={"column"} gap={"8px"}>
                <OrderHistoryInvoiceAccordion
                  isOpen={isOpen}
                  btnComponent={
                    <OrderHistorySubPriceInfo
                      variant={"FUNDS_REQUEST"}
                      amount={0}
                      status={"ERROR"}
                      hasData={false}
                    />
                  }
                ></OrderHistoryInvoiceAccordion>
              </Flex>
            )}
          </OrderHistoryInvoiceToggleBar>
        )}
        {data?.transportations.map((transportation, index) => {
          const transportationDetail = extractTransportationFromOrderHistory(
            toCamelKeyObject(transportation)
          );
          const {
            fundRequestAmount,
            isRemitted,
            fundRequestFileId,
            transportationPayment,
          } = transportationDetail;

          if (!transportationPayment) return null;

          return (
            <OrderHistoryInvoiceToggleBar
              key={transportation.id}
              name={transportation.name}
              index={index}
              amount={fundRequestAmount}
              defaultOpen={!isRemitted}
            >
              {({ isOpen }) => (
                <Flex flexDirection={"column"} gap={"8px"}>
                  <OrderHistoryInvoiceAccordion
                    isOpen={isOpen}
                    btnComponent={
                      <OrderHistorySubPriceInfo
                        variant={"FUNDS_REQUEST"}
                        amount={fundRequestAmount}
                        status={isRemitted ? "REMITTED" : "REMITTANCE_REQUIRED"}
                        onFileDownloadClick={() => {
                          if (!fundRequestFileId) return;
                          fireDownloadUnoFile(
                            {
                              unoFileId: fundRequestFileId,
                            },
                            {}
                          );
                        }}
                      />
                    }
                  >
                    <TransportationPanel
                      transportation={transportationDetail}
                    />
                  </OrderHistoryInvoiceAccordion>
                  {/*<OrderHistoryInvoiceAccordion*/}
                  {/*  isOpen={isOpen}*/}
                  {/*  btnComponent={<OrderHistorySubPriceInfo />}*/}
                  {/*>*/}
                  {/*  <ServiceFeeGrid />*/}
                  {/*</OrderHistoryInvoiceAccordion>*/}
                </Flex>
              )}
            </OrderHistoryInvoiceToggleBar>
          );
        })}
      </Flex>
    </Flex>
  );
}

export default OrderHistoryInvoiceIncidentalExpensesArticle;

interface TransportationPanelProps {
  transportation: ReturnType<typeof extractTransportationFromOrderHistory>;
}

const TransportationPanel = ({ transportation }: TransportationPanelProps) => {
  const { tTitle, isKor } = useI18nHelper();
  const { issuedOn, remittedOn } = transportation;

  return (
    <Flex flexDirection={"column"}>
      <Box padding={"8px 0px 12px 44px"}>
        <Box
          padding={"16px"}
          borderRadius={"8px"}
          boxShadow={"0 0 0 0.5px #D4D4D1"}
        >
          <UnorderedList textStyle={"caption-regular"} color={"#6F6F6C"}>
            <ListItem>
              {tTitle("order_history:invoice.issued_date")} :{" "}
              {formatDateToYMDEGeneral(issuedOn as string, isKor, "-")}
            </ListItem>
            {remittedOn && (
              <ListItem>
                {tTitle("order_history:invoice.remitted_date")} :{" "}
                {formatDateToYMDEGeneral(remittedOn as string, isKor, "-")}
              </ListItem>
            )}
          </UnorderedList>
        </Box>
      </Box>
    </Flex>
  );
};

const ServiceFreeGridTemplateColumns = `repeat(2, 1fr)`;

const ServiceFeeGrid = () => {
  return (
    <Box padding={"8px 0px 12px 44px"}>
      <Grid
        templateColumns={ServiceFreeGridTemplateColumns}
        padding={"16px"}
        borderRadius={"8px"}
        boxShadow={"0 0 0 0.5px #D4D4D1"}
        sx={{
          "& > *:nth-of-type(2n + 2)": {
            textAlign: "end",
          },
        }}
        textStyle={"body-sm-regular"}
        color={"#6F6F6C"}
      >
        <TitleGridItem height={"24px"}>항목</TitleGridItem>
        <TitleGridItem>금액</TitleGridItem>

        <GridItem height={"24px"}>인보이스 금액</GridItem>
        <GridItem>€8,000.00</GridItem>

        <GridItem height={"24px"}>크레딧 사용</GridItem>
        <GridItem>- €200.00</GridItem>

        <TitleGridItem
          paddingY={"6px"}
          background={"#F7F9FA"}
          boxShadow={"0 -0.5px 0 0 #D4D4D1"}
        >
          총 유로 금액
        </TitleGridItem>
        <GridItem
          paddingY={"6px"}
          background={"#F7F9FA"}
          boxShadow={"0 -0.5px 0 0 #D4D4D1"}
        >
          €7,800.00
        </GridItem>

        <GridItem height={"24px"}>환율</GridItem>
        <GridItem>₩1,733.00</GridItem>

        <TitleGridItem
          paddingY={"10px"}
          background={"#FFF7CC"}
          boxShadow={"0 -0.5px 0 0 #D4D4D1"}
        >
          총 징수 원화 금액
        </TitleGridItem>
        <GridItem
          paddingY={"10px"}
          background={"#FFF7CC"}
          boxShadow={"0 -0.5px 0 0 #D4D4D1"}
          textStyle={"body-md-semi-bold"}
          color={"#1272EF"}
        >
          ₩13,560,400
        </GridItem>

        <GridItem>
          <UnorderedList>
            <ListItem>서류 발행일 : 2025-05-30(금)</ListItem>
          </UnorderedList>
        </GridItem>
      </Grid>
    </Box>
  );
};

interface TitleGridItemProps extends GridItemProps {
  children?: React.ReactNode;
}

const TitleGridItem = ({ children, ...props }: TitleGridItemProps) => {
  return (
    <GridItem textStyle={"body-sm-medium"} color={"#444444"} {...props}>
      {children}
    </GridItem>
  );
};
